import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuFileSpreadsheet, LuLoaderCircle } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { Field, TextInput } from '../../../components/form/FormControls';
import { useTranslation } from '../../../i18n/LanguageContext';
import { HelpNote } from '../../ProfilePage/components/ProfileUi';

/**
 * "Detaylı Rapor": visits and jobs in a date range, as Excel, for the selected
 * employee or (no selection) every employee the user may see.
 */
const DetailedReportDialog = ({ isOpen, scope, employeeName, startDate, endDate, onStartDate, onEndDate, onGenerate, onClose }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`operations.report.${key}`, vars);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    await onGenerate();
    setBusy(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !busy && onClose()}
      size="md"
      title={label('title')}
      subtitle={label('subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" size="xs">
          <LuFileSpreadsheet aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>{label('cancel')}</Button>
          <Button variant="success" onClick={generate} disabled={busy} className="max-sm:tw-flex-1">
            {busy ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {busy ? label('generating') : label('generate')}
          </Button>
        </>
      )}
    >
      <div className="tw-space-y-4">
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('start')} htmlFor="operations-report-start" required>
            <TextInput id="operations-report-start" type="date" value={startDate} onChange={(e) => onStartDate(e.target.value)} />
          </Field>
          <Field label={label('end')} htmlFor="operations-report-end" required>
            <TextInput id="operations-report-end" type="date" value={endDate} min={startDate} onChange={(e) => onEndDate(e.target.value)} />
          </Field>
        </div>
        <HelpNote>{label(`help.${scope}`, { name: employeeName })}</HelpNote>
      </div>
    </Modal>
  );
};

DetailedReportDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  scope: PropTypes.oneOf(['employee', 'team', 'all']).isRequired,
  employeeName: PropTypes.string,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  onStartDate: PropTypes.func.isRequired,
  onEndDate: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DetailedReportDialog;
